import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Sparkles, Film, Users, Wallet } from "lucide-react";

export default function Login() {
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/app");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid credentials";
      setErr(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 flex">
      {/* Left: form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-sky-500 rounded-lg flex items-center justify-center shadow-sm">
              <span className="font-display font-black text-white text-lg">A</span>
            </div>
            <div>
              <div className="font-display font-bold text-[15px] text-slate-900">AIMOS</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">by Neuronex.ai</div>
            </div>
          </Link>
          <div>
            <h1 className="font-display font-bold text-3xl mb-2 text-slate-900">Welcome back.</h1>
            <p className="text-sm text-slate-500">Sign in to your brand workspace.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@brand.com"
                className="mt-1.5 border-slate-300 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-200 rounded-md h-11"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 border-slate-300 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-200 rounded-md h-11"
                required
              />
            </div>
            {err && <div className="text-xs text-red-600 font-mono">{err}</div>}
            <Button data-testid="login-submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-11 rounded-md shadow-sm">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </div>
          <div className="text-sm text-slate-600">
            No workspace yet?{" "}
            <Link to="/signup" data-testid="link-signup" className="text-blue-600 hover:underline font-medium">Start free — 100 tokens</Link>
          </div>
        </form>
      </div>

      {/* Right: value tiles */}
      <div className="hidden lg:flex w-1/2 relative bg-gradient-to-br from-blue-50 via-sky-50 to-white border-l border-slate-100 items-center justify-center p-12">
        <div className="max-w-md space-y-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-blue-600">// What you'll do here</div>
          <div className="font-display font-bold text-3xl text-slate-900 leading-tight">
            AI cinematic reels for every brand you run.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ValueTile icon={Users} label="24 AI influencers" sub="Or build your own" />
            <ValueTile icon={Film} label="Cinematic reels" sub="Fal Seedance + Nano Banana" />
            <ValueTile icon={Sparkles} label="URL → Reel" sub="Paste any product link" />
            <ValueTile icon={Wallet} label="100 free tokens" sub="Two reels on us" />
          </div>
          <div className="text-xs text-slate-500 pt-4 border-t border-slate-200">
            Trusted by AI-native marketing teams. Hindi + 8 languages. Direct-post to Instagram & TikTok (coming).
          </div>
        </div>
      </div>
    </div>
  );
}

function ValueTile({ icon: Icon, label, sub }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3.5 hover:border-blue-300 transition-colors">
      <Icon className="w-4 h-4 text-blue-600 mb-2" />
      <div className="font-display font-bold text-sm text-slate-900">{label}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
